import { __decorate } from "tslib";
import React, { Children } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import omit from 'lodash/omit';
import noop from 'lodash/noop';
import debounce from 'lodash/debounce';
import isString from 'lodash/isString';
import { computed, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import isPromise from 'is-promise';
import { ProgressType } from '../../../es/progress/enum';
import { getConfig } from '../../../es/configure';
import { getTooltip, getTooltipTheme } from '../../../es/_util/TooltipUtils';
import Icon from '../icon';
import FormContext from '../form/FormContext';
import Progress from '../progress';
import Ripple from '../ripple';
import { ButtonColor, ButtonTooltip, ButtonType, FuncType } from './enum';
import { DataSetStatus } from '../data-set/enum';
import { Size, WaitType } from '../core/enum';
import DataSetComponent from '../data-set/DataSetComponent';
import autobind from '../_util/autobind';
import { hide, show } from '../tooltip/singleton';
import isOverflow from '../overflow-tip/util';
let Button = class Button extends DataSetComponent {
    constructor(props, context) {
        super(props, context);
        this.handleClickWait = this.getHandleClick(props);
    }
    get loading() {
        const { type, dataSet, loading } = this.observableProps;
        return (loading ||
            (type === ButtonType.submit && !!dataSet && dataSet.status === DataSetStatus.submitting));
    }
    set loading(loading) {
        runInAction(() => {
            this.observableProps.loading = loading;
        });
    }
    getObservableProps(props, context) {
        return {
            ...super.getObservableProps(props, context),
            dataSet: 'dataSet' in props ? props.dataSet : context.dataSet,
            loading: 'loading' in props ? props.loading : this.observableProps ? this.loading : false,
            type: props.type,
            disabled: context.disabled || props.disabled,
        };
    }
    componentWillReceiveProps(nextProps, nextContext) {
        super.componentWillReceiveProps(nextProps, nextContext);
        const { wait, waitType } = this.props;
        if (wait !== nextProps.wait || waitType !== nextProps.waitType) {
            this.handleClickWait = this.getHandleClick(nextProps);
        }
    }
    componentWillUnmount() {
        this.handleClickWait.cancel();
        if (this.isTooltipShown) {
            hide();
            delete this.isTooltipShown;
        }
    }
    getHandleClick(props) {
        const { wait, waitType } = props;
        if (wait && waitType) {
            const options = { leading: true, trailing: true };
            if (waitType === WaitType.throttle) {
                options.trailing = false;
                options.maxWait = wait;
            }
            else if (waitType === WaitType.debounce) {
                options.leading = false;
            }
            return debounce(this.handleClick, wait, options);
        }
        return debounce(this.handleClick, 0);
    }
    handleClickIfBubble(e) {
        const { wait, waitType } = this.props;
        if (wait && waitType) {
            e.stopPropagation();
            this.handleClickWait(e);
        }
        else {
            this.handleClick(e);
        }
    }
    async handleClick(e) {
        const { onClick } = this.props;
        if (onClick) {
            const afterClick = onClick(e);
            if (isPromise(afterClick)) {
                try {
                    this.loading = true;
                    await afterClick;
                }
                finally {
                    this.loading = false;
                    // Fix asynchronous out of focus
                    this.focus();
                }
            }
        }
    }
    handleMouseEnter(e) {
        const { tooltip = getTooltip('button'), children } = this.props;
        const { element } = this;
        if (tooltip === ButtonTooltip.always || (tooltip === ButtonTooltip.overflow && isOverflow(element))) {
            show(element, {
                title: children,
                theme: getTooltipTheme('button'),
            });
            this.isTooltipShown = true;
        }
        const { onMouseEnter = noop } = this.props;
        onMouseEnter(e);
    }
    handleMouseLeave(e) {
        hide();
        const { onMouseLeave = noop } = this.props;
        onMouseLeave(e);
    }
    isDisabled() {
        return super.isDisabled() || this.loading;
    }
    getOmitPropsKeys() {
        return super.getOmitPropsKeys().concat([
            'icon',
            'funcType',
            'color',
            'loading',
            'wait',
            'waitType',
            'tooltip',
            'block',
        ]);
    }
    getOtherProps() {
        const otherProps = super.getOtherProps();
        const { tooltip = getTooltip('button') } = this.props;
        if (!this.disabled) {
            otherProps.onClick = this.handleClickIfBubble;
        }
        if (tooltip && [ButtonTooltip.always, ButtonTooltip.overflow].includes(tooltip)) {
            otherProps.onMouseEnter = this.handleMouseEnter;
            otherProps.onMouseLeave = this.handleMouseLeave;
        }
        return otherProps;
    }
    getClassName(...props) {
        const { prefixCls, props: { color = getConfig('buttonColor'), funcType = getConfig('buttonFuncType'), children, icon, block, }, } = this;
        const childrenCount = Children.count(children);
        return super.getClassName({
            [`${prefixCls}-${funcType}`]: funcType,
            [`${prefixCls}-${color}`]: color,
            [`${prefixCls}-icon-only`]: icon
                ? childrenCount === 0 || children === false
                : childrenCount === 1 && children.type && children.type.__C7N_ICON,
            [`${prefixCls}-block`]: block,
        }, ...props);
    }
    render() {
        const { children, icon, href, funcType } = this.props;
        const buttonIcon = this.loading ? (React.createElement(Progress, { key: "loading", type: ProgressType.loading, size: Size.small })) : (icon && React.createElement(Icon, { type: icon }));
        const hasString = Children.toArray(children).some(child => isString(child));
        const Cmp = href ? 'a' : 'button';
        const props = this.getMergedProps();
        const { disabled } = this;
        const { onMouseEnter, onMouseLeave } = props;
        const tooltipWrapper = disabled && !href && (onMouseEnter || onMouseLeave);
        const buttonProps = tooltipWrapper ? omit(props, ['className', 'style']) : props;
        const rippleDisabled = disabled || funcType === FuncType.link;
        const button = (React.createElement(Ripple, { disabled: rippleDisabled },
            React.createElement(Cmp, Object.assign({}, (href ? omit(buttonProps, ['type']) : buttonProps)),
                buttonIcon,
                hasString ? React.createElement("span", null, children) : children)));
        return tooltipWrapper ? (React.createElement("span", { className: classNames(props.className, `${this.prefixCls}-disabled-wrapper`), style: props.style, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave }, button)) : button;
    }
};
Button.displayName = 'Button';
// eslint-disable-next-line camelcase
Button.__PRO_BUTTON = true;
Button.contextType = FormContext;
Button.propTypes = {
    /**
     * 按钮展现模式
     * 可选值：'flat' | 'raised'
     * @default raised
     */
    funcType: PropTypes.oneOf([FuncType.flat, FuncType.raised, FuncType.link]),
    /**
     * 按钮颜色风格
     * 可选值：'default' | 'primary' | 'gray' | 'blue' | 'red' | 'green' | 'yellow' | 'purple' | 'dark'
     * @default 'default'
     */
    color: PropTypes.oneOf([
        ButtonColor.default,
        ButtonColor.primary,
        ButtonColor.gray,
        ButtonColor.blue,
        ButtonColor.red,
        ButtonColor.green,
        ButtonColor.yellow,
        ButtonColor.purple,
        ButtonColor.dark,
    ]),
    /**
     * 按钮类型
     * 可选值：'button' | 'submit' | 'reset'
     * @default 'button'
     */
    type: PropTypes.oneOf([ButtonType.button, ButtonType.submit, ButtonType.reset]),
    /**
     * 按钮是否是加载状态
     */
    loading: PropTypes.bool,
    /**
     * 点击跳转的地址，指定此属性 button 的行为和 a 链接一致
     */
    href: PropTypes.string,
    /**
     * 相当于 a 链接的 target 属性，href 存在时生效
     */
    target: PropTypes.string,
    /**
     * 点击等待时间
     */
    wait: PropTypes.number,
    /**
     * 点击间隔类型，可选值：throttle | debounce
     * @default throttle
     */
    waitType: PropTypes.oneOf([WaitType.throttle, WaitType.debounce]),
    /**
     * 用tooltip显示按钮内容
     * 可选值：`none` `always` `overflow`
     */
    tooltip: PropTypes.string,
    /**
     * 将按钮宽度调整为其父宽度的选项
     */
    block: PropTypes.bool,
    ...DataSetComponent.propTypes,
};
Button.defaultProps = {
    suffixCls: 'btn',
    type: ButtonType.button,
    waitType: WaitType.throttle,
};
__decorate([
    computed
], Button.prototype, "loading", null);
__decorate([
    autobind
], Button.prototype, "handleClickIfBubble", null);
__decorate([
    autobind
], Button.prototype, "handleClick", null);
__decorate([
    autobind
], Button.prototype, "handleMouseEnter", null);
__decorate([
    autobind
], Button.prototype, "handleMouseLeave", null);
Button = __decorate([
    observer
], Button);
export default Button;
